import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const CurrencyContext = createContext(null);

const fallbackCurrency = {
  code: "INR",
  country: "India",
  countryCode: "IN",
  rate: 1,
};

// Helper function to fetch IP location with fallback support
const fetchLocationData = async () => {
  // Primary API: ipapi.co
  try {
    const res = await fetch("https://ipapi.co/json/");
    if (!res.ok) throw new Error(`ipapi.co failed with status ${res.status}`);
    const data = await res.json();
    
    return {
      currency: data.currency,
      country: data.country_name,
      countryCode: data.country_code,
    };
  } catch (primaryError) {
    // Try the backup location service silently.
  }

  // Backup API: ip-api.com
  try {
    const res = await fetch("http://ip-api.com/json/?fields=status,country,countryCode,currency");
    if (!res.ok) throw new Error(`ip-api.com failed with status ${res.status}`);
    const data = await res.json();
    
    if (data.status !== "success") throw new Error("ip-api.com returned unsuccessful status");

    return {
      currency: data.currency,
      country: data.country,
      countryCode: data.countryCode,
    };
  } catch (backupError) {
    return null;
  }
};

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrency] = useState(fallbackCurrency);
  const [rates, setRates] = useState({ INR: 1 });
  const [loading, setLoading] = useState(true);
  const [exchangeRateError, setExchangeRateError] = useState("");
  const [exchangeRateUpdatedAt, setExchangeRateUpdatedAt] = useState("");

  useEffect(() => {
    let active = true;

    const detectVisitorCurrency = async () => {
      try {
        setLoading(true);

        // Fetch location (with fallback) and exchange rates in parallel
        const [locationData, ratesResponse] = await Promise.all([
          fetchLocationData(),
          fetch("https://open.er-api.com/v6/latest/INR").catch(() => {
            return null;
          }),
        ]);

        const ratesData = ratesResponse ? await ratesResponse.json() : null;

        if (!active) return;

        const exchangeRates = ratesData?.rates || {};
        const savedCountryCode = localStorage.getItem("selectedRegionCode");
        const savedCountry = localStorage.getItem("selectedRegion");
        const savedCurrencyCode = localStorage.getItem("selectedCurrencyCode");
        const requestedCode = savedCurrencyCode || locationData?.currency || "INR";
        const detectedCode = exchangeRates[requestedCode] ? requestedCode : "INR";
        const detectedRate = Number(exchangeRates[detectedCode] || 1);

        const activeCurrency = {
          code: detectedCode,
          country: savedCountry || locationData?.country || "India",
          countryCode: savedCountryCode || locationData?.countryCode || "IN",
          rate: detectedRate,
        };

        if (ratesData?.rates) {
          setRates(exchangeRates);
          setExchangeRateUpdatedAt(ratesData.time_last_update_utc || "");
        } else {
          setExchangeRateError("Live exchange rates could not be loaded. Prices are shown in the available base currency.");
        }
        
        setCurrency(activeCurrency);
      } catch (error) {
        if (active) {
          setCurrency(fallbackCurrency);
          setExchangeRateError("Live exchange rates could not be loaded. Prices are shown in the available base currency.");
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    detectVisitorCurrency();

    return () => {
      active = false;
    };
  }, []);

  const changeCountry = useCallback((country) => {
    const countryName = country?.name?.common;
    const countryCode = country?.cca2;
    const currencyCode = Object.keys(country?.currencies || {})[0];

    if (!countryName || !countryCode || !currencyCode) {
      throw new Error("This country does not have supported currency details.");
    }

    const rate = Number(rates[currencyCode]);
    if (!Number.isFinite(rate) || rate <= 0) {
      throw new Error(`A live exchange rate for ${currencyCode} is not available.`);
    }

    const selection = {
      code: currencyCode,
      country: countryName,
      countryCode,
      region: country.region || "",
      rate,
    };

    localStorage.setItem("selectedRegion", countryName);
    localStorage.setItem("selectedRegionCode", countryCode);
    localStorage.setItem("selectedCurrencyCode", currencyCode);
    setCurrency(selection);
    setExchangeRateError("");
    return selection;
  }, [rates]);

  const getCurrencySymbol = (currencyCode) => {
    try {
      return (
        new Intl.NumberFormat(undefined, {
          style: "currency",
          currency: currencyCode,
          currencyDisplay: "narrowSymbol",
        })
          .formatToParts(0)
          .find((part) => part.type === "currency")?.value || currencyCode
      );
    } catch {
      return currencyCode;
    }
  };

  const value = useMemo(
    () => ({
      ...currency,
      loading,
      rates,
      exchangeRateError,
      exchangeRateUpdatedAt,
      symbol: getCurrencySymbol(currency.code),
      changeCountry,

      convert: (inrValue) => {
        const converted = Number(inrValue || 0) * (rates[currency.code] || currency.rate || 1);
        return converted;
      },

      format: (inrValue) => {
        const converted = Number(inrValue || 0) * (rates[currency.code] || currency.rate || 1);
        try {
          const parts = new Intl.NumberFormat(undefined, {
            style: "currency",
            currency: currency.code,
            maximumFractionDigits: 2,
          }).formatToParts(converted);

          return parts
            .map((part) => part.type === "currency" ? `${part.value} ` : part.value)
            .join("")
            .trim();
        } catch {
          return `${getCurrencySymbol(currency.code)} ${converted.toFixed(2)}`;
        }
      },
    }),
    [currency, rates, loading, exchangeRateError, exchangeRateUpdatedAt, changeCountry]
  );

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () =>
  useContext(CurrencyContext) || {
    ...fallbackCurrency,
    loading: false,
    symbol: "₹",
    convert: (value) => Number(value || 0),
    format: (value) => `₹ ${Number(value || 0).toFixed(2)}`,
  };

export default CurrencyProvider;